import React, { useCallback, useContext, useState, useEffect, useMemo } from "react";
import {
  getDateString,
  formatDate,
  getDateInLocalTZFromDateStr,
} from "../../../../utils/datetime";
import {
  INTERACTION_COLORS,
  BORDERS,
  BACKGROUND_VARIANTS,
  PLANNING_TOOLTIP_MESSAGES,
} from "../../../../config";
import {
  getWorkerRoleColorClass,
  getWorkerRoleLabel,
} from "../../../../utils/business";
import { getScopeBlockHighlightHex } from "./helpers";
import DetailsIcons from "./DetailsIcons";
import { WorkerInitials } from "../../../../components/ui";
import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";
import ScopeHoverDetails from "./ScopeHoverDetails";
import { DISPATCH_DAY_LOCKED_UI } from "../constants";
import SkippedDayOverlay from "../../shared/SkippedDayOverlay";

/**
 * Displays a summary of a work scope and its details.
 *
 * Hovering a card with a linked Schedule block reports `{ id, colorHex }` via
 * `onBlockHover`; every card in the job row whose scope shares that block id
 * renders a highlight in the shared color (derived from the hovered card's
 * foreman role, like Schedule bar surfaces, falling back to teal-600).
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.scope - API object containing details about the work scope.
 * @param {{ id: number, colorHex: string }|null} [props.hoveredBlock] - Hovered Schedule-block link shared across the job row.
 * @param {Function} [props.onBlockHover] - Setter for the hovered Schedule-block link.
 * @param {Object} [props.job] Job row used for worker-modal catalog.
 * @param {"dispatch"|"report"} [props.display="dispatch"] Capture vs live Dispatch chrome.
 * @returns {JSX.Element} The rendered component.
 */
const ScopeSummaryBox = ({
  scope,
  job,
  display = "dispatch",
  hoveredBlock = null,
  onBlockHover = null,
}) => {
  const weeklyContext = useContext(WeeklyContext);
  const dispatchContext = useContext(DispatchContext);
  const applyScopePatch = weeklyContext?.applyScopePatch;

  // Safely destructure with defaults for when DispatchContext is not available
  // Memoize to prevent unnecessary re-renders
  /**
   * Weekly scopes from context, or an empty list when WeeklyContext is missing.
   *
   * @param {void}
   * @returns {Array<Object>}
   */
  const scopes = useMemo(
    () => weeklyContext?.scopes || [],
    [weeklyContext?.scopes]
  );
  const copyScopesDate = dispatchContext?.copyScopesDate || null;
  /**
   * Scope ids currently marked for bulk delete (highlights this card when included).
   *
   * @param {void}
   * @returns {Array<number|string>}
   */
  const scopesToDelete = useMemo(
    () => dispatchContext?.scopesToDelete || [],
    [dispatchContext?.scopesToDelete]
  );
  const openWorkerModal = dispatchContext?.openWorkerModal || null;
  const handleWorkerModalSubmit =
    dispatchContext?.handleWorkerModalSubmit || null;
  const dayDetails = dispatchContext?.dayDetails || null;
  const copyScopeOrigin = dispatchContext?.copyScopeOrigin || null;
  const isReadOnly = Boolean(dispatchContext?.isReadOnly);
  const toggleScopeSkipped = dispatchContext?.toggleScopeSkipped || null;
  const openScopeActionMenu = dispatchContext?.openScopeActionMenu || null;

  const copyScopesDateMatch = copyScopesDate
    ? getDateString(copyScopesDate) === scope.date
    : false;

  /**
   * True when Dispatch DayDetails for this scope's calendar day has locked set on the server.
   * @param {void}
   * @returns {void}
   */
  const isDayLocked = useMemo(() => {
    if (!dayDetails?.length) {
      return false;
    }
    return Boolean(
      dayDetails.find(
        (d) =>
          d.date ===
          formatDate(getDateInLocalTZFromDateStr(scope.date), "YYYY-MM-DD")
      )?.locked
    );
  }, [dayDetails, scope.date]);

  const canOpenScopeContextMenu =
    display === "dispatch" &&
    !isReadOnly &&
    !isDayLocked &&
    Boolean(openScopeActionMenu);

  const [selectedWorkerIDs, setSelectedWorkerIds] = useState(
    scope.worker_assignment.map((worker) => worker.id)
  );
  const [prevWorkerIDs, setPrevWorkerIds] = useState(selectedWorkerIDs);
  const [usedWorkers, setUsedWorkers] = useState([]);

  /**
   * Right-click opens the same action menu used by the edit scope card.
   * @param {MouseEvent} event Context-menu event from the summary card.
   * @returns {void}
   */
  const handleContextMenu = useCallback(
    (event) => {
      if (!canOpenScopeContextMenu) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      openScopeActionMenu({
        scope,
        date: scope.date,
        disableCopy: true,
        anchorRect: {
          top: rect.top + 4,
          right: rect.right - 8,
        },
      });
    },
    [canOpenScopeContextMenu, openScopeActionMenu, scope]
  );

  // Force update of selected workers as other components can change the worker list
  useEffect(() => {
    setSelectedWorkerIds(scope.worker_assignment.map((worker) => worker.id));
  }, [scope]);

  // Update used workers list when day details change
  useEffect(() => {
    if (dayDetails) {
      const day = dayDetails.find(
        (day) =>
          day.date ===
          formatDate(getDateInLocalTZFromDateStr(scope.date), "YYYY-MM-DD")
      );
      if (day) {
        setUsedWorkers([
          ...scopes
            .filter((s) => s.date === scope.date && !s.skipped)
            .flatMap((s) => s.worker_assignment),
          ...day.off_work,
        ]);
      }
    }
  }, [dayDetails, scope, scopes]);

  /** Ctrl/Cmd-click toggles skipped; a plain click opens the worker assignment modal. */
  /**
   * Ctrl/Cmd-click toggles skipped; a plain click opens the worker assignment modal.
   *
   * @param {import('react').MouseEvent} event Card click event.
   * @returns {void}
   */
  const handleCardClick = (event) => {
    if (isDayLocked) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && toggleScopeSkipped) {
      event.preventDefault();
      event.stopPropagation();
      void toggleScopeSkipped(scope);
      return;
    }
    // Only open modal if the handlers are available (i.e., in dispatch context)
    if (openWorkerModal && handleWorkerModalSubmit) {
      openWorkerModal({
        workers: job?.workers || [],
        onSubmit: (submittedWorkerIds) =>
          handleWorkerModalSubmit(
            scope.id,
            selectedWorkerIDs,
            submittedWorkerIds,
            setPrevWorkerIds,
            setSelectedWorkerIds
          ),
        date: scope.date,
        scope,
        usedWorkers,
        selectedWorkerIds: selectedWorkerIDs,
      });
    }
  };

  // Apply styling (role appropriate background color) and role text to workers.
  const stylizedWorkers = scope["worker_assignment"].map((worker) => {
    return {
      ...worker,
      worker_role_color_class: getWorkerRoleColorClass(worker),
      worker_roles: getWorkerRoleLabel(worker),
    };
  });

  // Sort workers by role, with Foreman first, then alphabetically by role.
  stylizedWorkers.sort((a, b) => {
    if (a.worker_roles.includes("Foreman")) return -1;
    if (b.worker_roles.includes("Foreman")) return 1;
    return a.worker_roles.localeCompare(b.worker_roles);
  });

  /**
   * Background/hover classes for report mode, pending delete, or copy-source highlight.
   *
   * @param {void}
   * @returns {string}
   */
  const getInteractionClasses = () => {
    if (display === "report") {
      return `bg-gray-200 hover:bg-gray-400`;
    }
    if (scopesToDelete && scopesToDelete.includes(scope.id)) {
      return INTERACTION_COLORS.danger.bg;
    }
    if (
      copyScopesDateMatch ||
      (copyScopeOrigin && copyScopeOrigin[0] === scope.id)
    ) {
      return `${INTERACTION_COLORS.success.bg} ${INTERACTION_COLORS.success.hover}`;
    }
    return `${INTERACTION_COLORS.neutral.bg} ${INTERACTION_COLORS.neutral.hover}`;
  };

  // Only make clickable if modal handlers are available
  const isInteractive = !isDayLocked && openWorkerModal && handleWorkerModalSubmit;

  /**
   * Report this card's Schedule block (with a highlight color derived from its
   * foreman role, teal-600 fallback) so same-block siblings light up.
   * @param {void}
   * @returns {void}
   */
  const handleBlockHoverStart = useCallback(() => {
    if (!onBlockHover) {
      return;
    }
    const blockId = scope.schedule_block?.id;
    if (blockId == null) {
      onBlockHover(null);
      return;
    }
    onBlockHover({ id: blockId, colorHex: getScopeBlockHighlightHex(scope) });
  }, [onBlockHover, scope]);

  /**
   * Clear the hovered Schedule block when the pointer leaves the card.
   * @param {void}
   * @returns {void}
   */
  const handleBlockHoverEnd = useCallback(() => {
    onBlockHover?.(null);
  }, [onBlockHover]);

  // Highlight when any card in the row hovers this card's Schedule block.
  const isBlockHighlighted =
    scope.schedule_block?.id != null &&
    hoveredBlock?.id === scope.schedule_block.id;

  // Applied to content pieces rather than the card root: opacity on the root
  // would create a stacking context that traps the ScopeHoverDetails popover
  // behind sibling cards and makes it semi-transparent.
  const lockedMutedClass = isDayLocked
    ? DISPATCH_DAY_LOCKED_UI.scopeCardMutedClass
    : "";
  return (
    <div
      onClick={isInteractive || (!isDayLocked && toggleScopeSkipped) ? handleCardClick : undefined}
      onContextMenu={handleContextMenu}
      onMouseEnter={handleBlockHoverStart}
      onMouseLeave={handleBlockHoverEnd}
      title={
        isDayLocked
          ? PLANNING_TOOLTIP_MESSAGES.editingDisabledWhenLocked
          : "Click to Edit Workers"
      }
      className={`relative py-[2px] mt-1 ${getInteractionClasses()} ${BORDERS.radius.sm} ${isDayLocked ? "cursor-default" : isInteractive || display === "report" ? "cursor-pointer" : "cursor-default"}`}
      style={
        isBlockHighlighted
          ? // 8-digit hex: colorHex + "80" = same color at 50% opacity.
            { boxShadow: `0 0 5px 3px ${hoveredBlock.colorHex}80` }
          : undefined
      }
    >
      {scope.skipped ? (
        <SkippedDayOverlay
          aspectId={scope.id}
          reason={scope.skipped_reason ?? ""}
          module="dispatch"
          onReasonChange={(nextReason) =>
            applyScopePatch?.(scope.id, { skipped_reason: nextReason })
          }
        />
      ) : null}
      <div className="">
        <div className="flex items-center justify-between px-2 min-h-[24px]">
          {/* Empty title suppresses the card's native tooltip here so it
              does not cover the ScopeHoverDetails popover. No z-index here:
              raising this would trap the popover in a per-card stacking
              context and let sibling cards paint over it.
              The popover is left-0/right-0 against the card so it always
              matches the card width and alignment. */}
          <div
            title=""
            className="font-bold leading-tight min-h-[24px] pt-[2px] hover:underline group/scope min-w-0 flex-1 overflow-visible"
          >
            <div className={`truncate ${lockedMutedClass}`}>
              {scope.scope ? scope.scope?.title : "No Scope Set"}
            </div>

            <div
              id={`tooltip=${scope.job.id}`}
              className="scale-y-0 mt-1 z-20 absolute left-0 right-0 origin-top group-hover/scope:scale-y-100 duration-300 transition-transform ease-in-out"
            >
              <ScopeHoverDetails
                scope={scope}
                className={`pointer-cursor ${BACKGROUND_VARIANTS.dark.bg} shadow-black drop-shadow rounded-b`}
                weeklyContext={true}
              />
            </div>
          </div>
          <div
            className={`flex items-center text-sm flex-wrap text-nowrap group/scope ${lockedMutedClass}`}
          >
            {scope.quantity ? (
              `${scope.quantity} ${scope.scope?.unit?.name || "Qty"}`
            ) : (
              <DetailsIcons scope={scope} />
            )}
          </div>
        </div>
      </div>

      <div className={`flex flex-wrap justify-between px-2 ${lockedMutedClass}`}>
        {stylizedWorkers.length > 0 ? (
          <div className="flex flex-wrap pt-1">
            {stylizedWorkers.map((worker, index) => (
              <WorkerInitials
                key={index}
                worker={worker}
                className="mb-[2px]"
                colorByRole={true}
                tooltip
                // TODO: prevWorkerIDs is intended to be used in an animation to show which workers
                // were added/removed. This function should be added to worker initials.
                prevIDs={prevWorkerIDs}
              />
            ))}
          </div>
        ) : null}
        <div className="flex items-center flex-wrap">
          {scope.quantity ? <DetailsIcons scope={scope} /> : null}
        </div>
      </div>
      {isDayLocked ? (
        // Starts below the 24px header row so the scope title stays hoverable
        // (ScopeHoverDetails still opens on locked days). Clicks on the title
        // are inert anyway: the card's onClick is removed while locked.
        <div
          className="absolute inset-x-0 bottom-0 top-6 z-[5]"
          title={PLANNING_TOOLTIP_MESSAGES.editingDisabledWhenLocked}
          aria-label={PLANNING_TOOLTIP_MESSAGES.editingDisabledWhenLocked}
        />
      ) : null}
    </div>
  );
};

export default ScopeSummaryBox;
